// FiltersSidebar.tsx - VERSION CORRIGÉE
import { RotateCcw, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSelectOptions, getTitleEnumValue } from '@/functions';
import { useUrlParams } from '../hooks';
import { useCallback, useState } from 'react';

const FiltersSidebar = () => {
  const {
    gender,
    accountStatus,
    certified,
    transactionId,
    sortBy,
    setValue,
    deleteValue,
    setArrayParam,
    updateSearchParams
  } = useUrlParams();

  const [transactionInputValue, setTransactionInputValue] = useState(transactionId || '');

  
  
  const handleGenderChange = useCallback((genderValue: string, checked: boolean) => {
    const newGender = checked
      ? [...gender, genderValue]
      : gender.filter((g: string) => g !== genderValue);

    
    if (newGender.length === 0) {
      deleteValue('gender');
    } else {
      setArrayParam('gender', newGender);
    }
  }, [gender, deleteValue, setArrayParam]);

  const handleAccountStatusChange = useCallback((statusValue: string, checked: boolean) => {
    if (statusValue === 'certified') {
      // Handle certified separately
      if (checked) {
        setValue('certified', 'true');
      } else {
        deleteValue('certified');
      }
      return;
    }

    const newStatus = checked
      ? [...accountStatus, statusValue]
      : accountStatus.filter((s: string) => s !== statusValue);

    if (newStatus.length === 0) {
      deleteValue('accountStatus');
    } else {
      setArrayParam('accountStatus', newStatus);
    }
  }, [accountStatus, deleteValue, setArrayParam, setValue]);

  const handleTransactionSearch = useCallback(() => {
    if (transactionInputValue.trim()) {
      setValue('transactionId', transactionInputValue.trim());
    } else {
      deleteValue('transactionId');
    }
  }, [transactionInputValue, setValue, deleteValue]);

  const handleClearTransaction = useCallback(() => {
    setTransactionInputValue('');
    deleteValue('transactionId');
  }, [deleteValue]);

  const handleSortChange = useCallback((value: string) => {
    setValue('sortBy', value);
  }, [setValue]);

  const handleReset = useCallback(() => {
    updateSearchParams(l => {
      // Supprimer tous les paramètres de recherche/filtrage
      l.delete('searchQuery');
      l.delete('sortType');
      l.delete('sortBy');
      l.delete('gender');
      l.delete('accountStatus');
      l.delete('certified');
      l.delete('transactionId');
      l.delete('page');
      // Ajouter seulement les valeurs par défaut
      l.append('accountStatus', 'ACTIVE');
      l.append('accountStatus', 'PAUSED');
      l.append('gender', 'MALE');
      l.append('gender', 'FEMALE');
      return l;
    });
  }, [updateSearchParams]);

  const genderOptions = getSelectOptions('GenderEnum');
  const statusOptions = [
    ...getSelectOptions('UserAccountStatusEnum').filter(option =>
      option.value !== 'ADMIN_DELETED' && option.value !== 'HIDDEN'
    ),
    { title: 'Certifié', value: 'certified' }
  ];

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold hidden md:block">Filtres</h3>
        <Button variant="outline" size="sm" onClick={handleReset}>
          <RotateCcw size={16} className="mr-2" />
          Reset
        </Button>
      </div>

      {/* Transaction ID Search */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Recherche par Transaction ID</h4>
        <div className="flex space-x-2">
          <div className="relative flex-1">
            <Input
              placeholder="Transaction ID"
              value={transactionInputValue}
              onChange={(e) => setTransactionInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTransactionSearch()}
              className="pr-8"
            />
            {transactionInputValue && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={handleClearTransaction}
              >
                <X size={14} />
              </Button>
            )}
          </div>
          <Button onClick={handleTransactionSearch} size="sm">
            <Search size={16} />
          </Button>
        </div>
      </div>

      

      {/* Sort Options */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Trier par</h4>
        <Select value={sortBy || 'createdAt'} onValueChange={handleSortChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="createdAt">Plus récent</SelectItem>
            <SelectItem value="-createdAt">Plus ancien</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Gender Filter */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Genre</h4>
        <div className="space-y-2">
          {genderOptions.filter(option => option.value).map(option => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`gender-${option.value}`}
                checked={gender.includes(option.value!)}
                onCheckedChange={(checked) => 
                  handleGenderChange(option.value!, checked as boolean)
                }
              />
              <label htmlFor={`gender-${option.value}`} className="text-sm font-medium">
                {getTitleEnumValue('GenderEnum', option.value!)}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Account Status Filter */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Statut du compte</h4>
        <div className="space-y-2">
          {statusOptions.filter(option => option.value).map(option => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`status-${option.value}`}
                checked={option.value === 'certified' ? certified === 'true' : accountStatus.includes(option.value!)}
                onCheckedChange={(checked) =>
                  handleAccountStatusChange(option.value!, checked as boolean)
                }
              />
              <label htmlFor={`status-${option.value}`} className="text-sm font-medium">
                {option.value === 'certified' ? option.title : getTitleEnumValue('UserAccountStatusEnum', option.value!)}
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FiltersSidebar;
